import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { CHARACTERS, characterId, type CharacterId } from '../content/characters';

const ANIMATION_PATHS = [
  'models/kaykit/animations/Rig_Medium_General.glb',
  'models/kaykit/animations/Rig_Medium_MovementBasic.glb',
  'models/kaykit/animations/Rig_Medium_MovementAdvanced.glb',
] as const;

export type CharacterMotion = 'idle' | 'run' | 'dash' | 'hit';

function baseUrl(): string {
  try {
    const env = (import.meta as { env?: { BASE_URL?: string } }).env;
    return env?.BASE_URL ?? './';
  } catch {
    return './';
  }
}

function resolveUrl(path: string): string {
  const base = baseUrl();
  return (base.endsWith('/') ? base : base + '/') + path.replace(/^\/+/, '');
}

/** One independently cloned/skeleton-bound KayKit avatar. */
export class AnimatedCharacter {
  readonly object = new THREE.Group();
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<CharacterMotion, THREE.AnimationAction>();
  private readonly materials: THREE.MeshStandardMaterial[] = [];
  private current: CharacterMotion | null = null;
  private wasHit = false;
  private hitTime = 0;

  constructor(template: THREE.Object3D, clips: Map<string, THREE.AnimationClip>) {
    const model = cloneSkeleton(template);
    model.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(model);
    const height = Math.max(0.001, box.max.y - box.min.y);
    const scale = 1.8 / height;
    model.scale.setScalar(scale);
    model.position.set(-(box.min.x + box.max.x) * 0.5 * scale, -box.min.y * scale, -(box.min.z + box.max.z) * 0.5 * scale);

    // Each avatar gets independent materials so hit emissive never leaks to a
    // second local player. Preserve KayKit's embedded gradient-atlas texture.
    model.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = false;
      const source = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const cloned = source.map((material) => {
        const mat = material.clone();
        if (mat instanceof THREE.MeshStandardMaterial) {
          mat.roughness = 0.82;
          mat.metalness = 0.02;
          this.materials.push(mat);
        }
        return mat;
      });
      mesh.material = Array.isArray(mesh.material) ? cloned : cloned[0];
    });

    this.object.add(model);
    this.mixer = new THREE.AnimationMixer(model);
    const bind = (motion: CharacterMotion, clipName: string) => {
      const clip = clips.get(clipName);
      if (!clip) return;
      const action = this.mixer.clipAction(clip);
      action.enabled = true;
      if (motion === 'dash' || motion === 'hit') {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      this.actions.set(motion, action);
    };
    bind('idle', 'Idle_A');
    bind('run', 'Running_A');
    bind('dash', 'Dodge_Forward');
    bind('hit', 'Hit_A');
    this.play('idle', true);
  }

  update(dt: number, requested: Exclude<CharacterMotion, 'hit'>, hitFlash: number): void {
    const hit = hitFlash > 0;
    if (hit && !this.wasHit) this.hitTime = 0.22;
    this.wasHit = hit;
    this.hitTime = Math.max(0, this.hitTime - dt);
    this.play(this.hitTime > 0 ? 'hit' : requested);
    this.mixer.update(dt);
    for (const mat of this.materials) {
      mat.emissive.setHex(hit ? 0xffffff : 0x000000);
      mat.emissiveIntensity = hit ? 0.85 : 1;
    }
  }

  /** Release per-instance animation and material state. Geometry and textures
   * belong to CharacterLibrary's shared templates and intentionally survive. */
  dispose(): void {
    this.mixer.stopAllAction();
    for (const mat of this.materials) mat.dispose();
    this.object.removeFromParent();
  }

  private play(next: CharacterMotion, immediate = false): void {
    if (this.current === next) return;
    const previous = this.current ? this.actions.get(this.current) : undefined;
    const action = this.actions.get(next) ?? this.actions.get('idle');
    if (!action) return;
    previous?.fadeOut(immediate ? 0 : 0.1);
    action.reset().fadeIn(immediate ? 0 : 0.1).play();
    this.current = next;
  }
}

/** Loads the five selected KayKit characters and the shared medium-rig clips. */
export class CharacterLibrary {
  private readonly loader = new GLTFLoader();
  private readonly models = new Map<CharacterId, THREE.Object3D>();
  private readonly clips = new Map<string, THREE.AnimationClip>();

  async preload(onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const jobs = [
      ...CHARACTERS.map((def) => ({ kind: 'model' as const, id: def.id, path: def.model })),
      ...ANIMATION_PATHS.map((path) => ({ kind: 'animations' as const, path })),
    ];
    let loaded = 0;
    await Promise.all(jobs.map(async (job) => {
      try {
        const gltf = await this.loader.loadAsync(resolveUrl(job.path));
        if (job.kind === 'model') this.models.set(job.id, gltf.scene);
        else for (const clip of gltf.animations) this.clips.set(clip.name, clip);
      } catch (error) {
        console.warn(`CharacterLibrary: failed to load ${job.path}`, error);
      } finally {
        loaded++;
        onProgress?.(loaded, jobs.length);
      }
    }));
    console.info(`CharacterLibrary: loaded ${this.models.size}/${CHARACTERS.length} character(s), ${this.clips.size} animation clip(s)`);
  }

  create(id: unknown): AnimatedCharacter | null {
    const template = this.models.get(characterId(id));
    return template ? new AnimatedCharacter(template, this.clips) : null;
  }
}
